import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Package,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Edit,
  Plus,
  X,
  DollarSign,
  User,
  Image,
  Calendar,
  MapPin,
  Mail,
  ExternalLink,
  Bell,
  Send,
  Phone
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import PurchaseConfirmationPreview from './PurchaseConfirmationPreview';
import '../styles/OrdersAdmin.css';

const OrdersAdmin = () => {
  const navigate = useNavigate();
  
  // State management
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    paid: 0,
    fulfilled: 0,
    cancelled: 0,
    totalRevenue: 0
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [productTypeFilter, setProductTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [sortBy, setSortBy] = useState('purchased_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  // Order detail modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  
  // Create / Edit order modal
  const ORDER_FORM_DEFAULTS = {
    product_name: '',
    product_type: 'original',
    price: '',
    print_size: '',
    vc_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    notes: '',
    fulfillment_status: 'paid',
    purchased_at: new Date().toISOString().slice(0, 16),
    stripe_payment_id: '',
    sale_platform: 'Stripe Terminal',
    sale_location: 'Stan Mansion',
    event_id: '',
  };
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null); // null = new, obj = edit
  const [orderForm, setOrderForm] = useState(ORDER_FORM_DEFAULTS);
  const [savingOrder, setSavingOrder] = useState(false);
  
  // Confirmation preview modal
  const [confirmationPreviewOpen, setConfirmationPreviewOpen] = useState(false);
  const [orderForConfirmation, setOrderForConfirmation] = useState(null);
  const [enrichedOrders, setEnrichedOrders] = useState([]);

  // Helper to get Cloudinary thumbnail URL
  const getThumbUrl = (coverImage) => {
    if (!coverImage) return null;
    // Insert t_fitmax-300/ transformation before the filename
    const parts = coverImage.split('/upload/');
    if (parts.length === 2) {
      return `${parts[0]}/upload/t_fitmax-300/${parts[1]}`;
    }
    return coverImage;
  };

  // Calculate pagination range
  const from = (currentPage - 1) * pageSize;
  const to = from + pageSize - 1;

  // Fetch orders data
  const fetchOrders = useCallback(async () => {
    setLoading(true);
    
    try {
      // Build count query
      let countQuery = supabase
        .from('orders')
        .select('*', { count: 'exact', head: true });
      
      // Apply filters to count
      if (statusFilter !== 'all') {
        countQuery = countQuery.eq('fulfillment_status', statusFilter);
      }
      if (productTypeFilter !== 'all') {
        countQuery = countQuery.ilike('product_type', productTypeFilter);
      }
      if (searchQuery) {
        countQuery = countQuery.or(`product_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,order_num.ilike.%${searchQuery}%`);
      }
      if (dateRange.start) {
        countQuery = countQuery.gte('purchased_at', dateRange.start);
      }
      if (dateRange.end) {
        countQuery = countQuery.lte('purchased_at', dateRange.end + 'T23:59:59');
      }
      
      const { count } = await countQuery;
      setTotalCount(count || 0);
      
      // Build data query
      let query = supabase
        .from('orders')
        .select('*')
        .range(from, to)
        .order(sortBy, { ascending: sortOrder === 'asc' });
      
      // Apply filters
      if (statusFilter !== 'all') {
        query = query.eq('fulfillment_status', statusFilter);
      }
      if (productTypeFilter !== 'all') {
        query = query.ilike('product_type', productTypeFilter);
      }
      if (searchQuery) {
        query = query.or(`product_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%,first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,order_num.ilike.%${searchQuery}%`);
      }
      if (dateRange.start) {
        query = query.gte('purchased_at', dateRange.start);
      }
      if (dateRange.end) {
        query = query.lte('purchased_at', dateRange.end + 'T23:59:59');
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      // Enrich orders with artwork images and contact info
      if (data && data.length > 0) {
        const vcIds = [...new Set(data.filter(o => o.vc_id).map(o => o.vc_id))];
        const buyerUuids = [...new Set(data.filter(o => o.uuid).map(o => o.uuid))];
        
        // Fetch artwork info (including artist uuid from vcs.uuid)
        let artworkMap = {};
        if (vcIds.length > 0) {
          const { data: artworks } = await supabase
            .from('vcs')
            .select('vc_id, cover_image, title, uuid')
            .in('vc_id', vcIds);
          if (artworks) {
            artworkMap = Object.fromEntries(artworks.map(a => [a.vc_id, a]));
          }
        }
        
        // Get artist uuids from artwork (vcs.uuid -> users.uuid)
        const artistUuids = [...new Set(Object.values(artworkMap).filter(a => a.uuid).map(a => a.uuid))];
        console.log('Artist UUIDs to fetch:', artistUuids);
        
        // Fetch artist info using vcs.uuid
        let artistMap = {};
        if (artistUuids.length > 0) {
          const { data: artists, error: artistError } = await supabase
            .from('users')
            .select('uuid, email, mobile_phone, first_name, last_name, nickname')
            .in('uuid', artistUuids);
          console.log('Artists fetched:', artists, 'Error:', artistError);
          if (artists && artists.length > 0) {
            artistMap = Object.fromEntries(artists.map(a => [a.uuid, a]));
            console.log('Artist map:', artistMap);
          }
        }
        
        // Fetch buyer info
        let buyerMap = {};
        if (buyerUuids.length > 0) {
          const { data: buyers } = await supabase
            .from('users')
            .select('uuid, email, mobile_phone, first_name, last_name, nickname')
            .in('uuid', buyerUuids);
          if (buyers) {
            buyerMap = Object.fromEntries(buyers.map(b => [b.uuid, b]));
          }
        }
        
        // Merge data - artist info comes from vcs.uuid -> users
        const enriched = data.map(order => {
          const artwork = artworkMap[order.vc_id] || null;
          const artistUuid = artwork?.uuid;
          return {
            ...order,
            artwork,
            artistInfo: artistUuid ? artistMap[artistUuid] : null,
            buyerInfo: buyerMap[order.uuid] || null
          };
        });
        
        setEnrichedOrders(enriched);
        setOrders(data);
      } else {
        setEnrichedOrders([]);
        setOrders(data || []);
      }
      
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, statusFilter, productTypeFilter, searchQuery, dateRange, sortBy, sortOrder, from, to]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      // Get counts by status
      const { data: allOrders, error } = await supabase
        .from('orders')
        .select('fulfillment_status, price');
      
      if (error) throw error;
      
      const statsData = {
        total: allOrders.length,
        pending: allOrders.filter(o => o.fulfillment_status === 'pending').length,
        paid: allOrders.filter(o => o.fulfillment_status === 'paid').length,
        fulfilled: allOrders.filter(o => o.fulfillment_status === 'fulfilled').length,
        cancelled: allOrders.filter(o => o.fulfillment_status === 'cancelled').length,
        totalRevenue: allOrders
          .filter(o => o.fulfillment_status !== 'cancelled')
          .reduce((sum, o) => sum + (parseFloat(o.price) || 0), 0)
      };
      
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [fetchOrders]);

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdating(true);
    try {
      const { error } = await supabase
        .from('orders')
        .update({ fulfillment_status: newStatus })
        .eq('id', orderId);
      
      if (error) throw error;
      
      // Refresh data
      await fetchOrders();
      await fetchStats();
      
      // Update selected order if open
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({ ...selectedOrder, fulfillment_status: newStatus });
      }
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Failed to update order status');
    } finally {
      setUpdating(false);
    }
  };

  // Open the purchase confirmation preview modal
  const openConfirmationPreview = (order) => {
    // Find enriched order with artistInfo
    const enrichedOrder = enrichedOrders.find(o => o.id === order.id) || order;
    setOrderForConfirmation(enrichedOrder);
    setConfirmationPreviewOpen(true);
  };

  // Send purchase confirmation notifications (called from preview modal)
  const sendPurchaseConfirmation = async (payload) => {
    setSendingNotifications(true);
    const order = payload.order;
    const customContent = payload.customContent;
    
    try {
      // Get artist info from enriched order or fetch if needed
      let artistEmail = order.artistInfo?.email || null;
      let artistName = order.artistInfo?.nickname || 
        `${order.artistInfo?.first_name || ''} ${order.artistInfo?.last_name || ''}`.trim() || 
        'Artist';
      let artistUuid = order.artwork?.uuid || null;
      
      // If no artist info in order, try to fetch from artwork
      if (!artistEmail && order.vc_id) {
        const { data: vcData } = await supabase
          .from('vcs')
          .select('uuid')
          .eq('vc_id', order.vc_id)
          .single();
        
        if (vcData?.uuid) {
          artistUuid = vcData.uuid;
          const { data: artistData } = await supabase
            .from('users')
            .select('email, nickname, first_name, last_name')
            .eq('uuid', vcData.uuid)
            .single();
          
          if (artistData) {
            artistEmail = artistData.email;
            artistName = artistData.nickname || `${artistData.first_name || ''} ${artistData.last_name || ''}`.trim() || 'Artist';
          }
        }
      }

      // Validate required fields before sending
      const buyerEmail = order.email;
      const artworkTitle = order.product_name || 'Artwork';
      const finalArtistName = artistName || 'Artist';
      const orderNum = order.order_num || `ORD-${order.id}`;
      const amount = parseFloat(order.price);
      
      if (!buyerEmail) {
        throw new Error('Buyer email is required to send confirmation');
      }
      
      if (isNaN(amount)) {
        throw new Error('Valid order amount is required');
      }

      const apiPayload = {
        buyer_email: buyerEmail,
        buyer_first_name: order.first_name || 'Customer',
        buyer_uuid: order.uuid || null,
        artwork_title: artworkTitle,
        artist_name: finalArtistName,
        artist_email: artistEmail,
        artist_uuid: artistUuid,
        amount: amount,
        order_id: order.id,
        order_num: orderNum,
        product_type: order.product_type || 'original',
        print_size: order.print_size || null,
        // Pass custom content for overrides - all fields from preview modal
        custom_buyer_subject: customContent?.buyerEmail?.subject,
        custom_buyer_header: customContent?.buyerEmail?.header,
        custom_buyer_subheader: customContent?.buyerEmail?.subheader,
        custom_buyer_greeting: customContent?.buyerEmail?.greeting,
        custom_buyer_message: customContent?.buyerEmail?.thankYouMessage,
        custom_fulfillment_message: customContent?.buyerEmail?.fulfillmentMessage,
        custom_artist_subject: customContent?.artistEmail?.subject,
        custom_artist_header: customContent?.artistEmail?.header,
        custom_artist_message: customContent?.artistEmail?.greeting,
        custom_artist_next_steps: customContent?.artistEmail?.nextStepsMessage
      };

      console.log('Sending purchase confirmation with payload:', apiPayload);

      const { data, error } = await supabase.functions.invoke('send_purchase_confirmation', {
        body: apiPayload
      });

      if (error) throw error;

      alert(`Notifications sent!\n• Buyer email: ${data.results?.buyer_email_sent ? '✓' : '✗'}\n• Buyer push: ${data.results?.buyer_push_sent ? '✓' : '✗'}\n• Artist email: ${data.results?.artist_email_sent ? '✓' : '✗'}`);
      
      // Close the preview modal
      setConfirmationPreviewOpen(false);
      setOrderForConfirmation(null);
      
      // Refresh order data
      await fetchOrders();
      
      // Update selected order with new timestamps
      if (selectedOrder && selectedOrder.id === order.id) {
        const { data: updatedOrder } = await supabase
          .from('orders')
          .select('*')
          .eq('id', order.id)
          .single();
        if (updatedOrder) {
          // Find enriched data for updated order
          const enriched = enrichedOrders.find(o => o.id === updatedOrder.id);
          setSelectedOrder({
            ...updatedOrder,
            artwork: enriched?.artwork,
            artistInfo: enriched?.artistInfo,
            buyerInfo: enriched?.buyerInfo
          });
        }
      }
    } catch (error) {
      console.error('Error sending notifications:', error);
      alert('Failed to send notifications: ' + error.message);
    } finally {
      setSendingNotifications(false);
    }
  };

  // Open modal for new order
  const openNewOrderModal = () => {
    setEditingOrder(null);
    setOrderForm(ORDER_FORM_DEFAULTS);
    setOrderModalOpen(true);
  };

  // Open modal to edit an existing order
  const openEditOrderModal = (order) => {
    setEditingOrder(order);
    setOrderForm({
      product_name: order.product_name || '',
      product_type: order.product_type || 'original',
      price: order.price?.toString() || '',
      print_size: order.print_size || '',
      vc_id: order.vc_id?.toString() || '',
      first_name: order.first_name || '',
      last_name: order.last_name || '',
      email: order.email || '',
      phone: order.phone || '',
      notes: order.notes || '',
      fulfillment_status: order.fulfillment_status || 'paid',
      purchased_at: order.purchased_at ? new Date(order.purchased_at).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
      stripe_payment_id: order.stripe_payment_id || '',
      sale_platform: order.sale_platform || 'Stripe Terminal',
      sale_location: order.sale_location || '',
      event_id: order.event_id?.toString() || '',
    });
    setOrderModalOpen(true);
  };

  // Save (insert or update) an order
  const saveOrder = async () => {
    if (!orderForm.product_name || !orderForm.price) return;
    setSavingOrder(true);
    try {
      const payload = {
        product_name: orderForm.product_name,
        product_type: orderForm.product_type || 'original',
        price: parseFloat(orderForm.price),
        print_size: orderForm.print_size || null,
        vc_id: orderForm.vc_id ? parseInt(orderForm.vc_id) : null,
        first_name: orderForm.first_name || null,
        last_name: orderForm.last_name || null,
        email: orderForm.email || null,
        phone: orderForm.phone || null,
        notes: orderForm.notes || null,
        fulfillment_status: orderForm.fulfillment_status,
        purchased_at: orderForm.purchased_at ? new Date(orderForm.purchased_at).toISOString() : new Date().toISOString(),
        stripe_payment_id: orderForm.stripe_payment_id || null,
        sale_platform: orderForm.sale_platform || null,
        sale_location: orderForm.sale_location || null,
        event_id: orderForm.event_id ? parseInt(orderForm.event_id) : null,
        is_manual_entry: true,
        updated_at: new Date().toISOString(),
      };

      if (editingOrder) {
        const { error } = await supabase.from('orders').update(payload).eq('id', editingOrder.id);
        if (error) throw error;
      } else {
        payload.order_num = `VC-MAN-${Date.now()}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;
        payload.created_at = new Date().toISOString();
        const { error } = await supabase.from('orders').insert(payload);
        if (error) throw error;
      }

      setOrderModalOpen(false);
      setEditingOrder(null);
      setOrderForm(ORDER_FORM_DEFAULTS);
      await fetchOrders();
      await fetchStats();
    } catch (err) {
      console.error('Error saving order:', err);
      alert('Failed to save order: ' + err.message);
    }
    setSavingOrder(false);
  };

  // Export orders to CSV
  const exportToCSV = () => {
    const headers = ['Order #', 'Product', 'Type', 'Price', 'Customer', 'Email', 'Status', 'Purchase Date', 'Stripe ID'];
    const csvData = orders.map(order => [
      order.order_num || '',
      order.product_name || '',
      order.product_type || '',
      order.price || '',
      `${order.first_name || ''} ${order.last_name || ''}`.trim(),
      order.email || '',
      order.fulfillment_status || '',
      order.purchased_at ? new Date(order.purchased_at).toLocaleDateString() : '',
      order.stripe_payment_id || ''
    ]);
    
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusConfig = {
      pending: { icon: Clock, color: 'orange', label: 'Pending' },
      paid: { icon: DollarSign, color: 'green', label: 'Paid' },
      fulfilled: { icon: CheckCircle, color: 'blue', label: 'Fulfilled' },
      shipped: { icon: Truck, color: 'purple', label: 'Shipped' },
      cancelled: { icon: XCircle, color: 'red', label: 'Cancelled' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.icon;
    
    return (
      <span className={`status-badge status-${config.color}`}>
        <IconComponent size={14} />
        {config.label}
      </span>
    );
  };

  // Product type badge
  const ProductTypeBadge = ({ type }) => {
    const isOriginal = type?.toLowerCase() === 'original';
    return (
      <span className={`product-type-badge ${isOriginal ? 'original' : 'print'}`}>
        {isOriginal ? 'Original' : 'Print'}
      </span>
    );
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Pagination
  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="orders-admin">
      {/* Stats Cards */}
      <div className="orders-stats">
        <div className="stat-card">
          <div className="stat-icon total">
            <Package size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Orders</span>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon pending">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.pending}</span>
            <span className="stat-label">Pending</span>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon paid">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.paid}</span>
            <span className="stat-label">Paid</span>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon fulfilled">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.fulfilled}</span>
            <span className="stat-label">Fulfilled</span>
          </div>
        </div>
        
        <div className="stat-card revenue">
          <div className="stat-icon revenue">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{formatCurrency(stats.totalRevenue)}</span>
            <span className="stat-label">Total Revenue</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="orders-toolbar">
        <div className="toolbar-left">
          <div className="search-wrapper">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search orders, customers, emails..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          
          <button 
            className={`filter-toggle ${filtersOpen ? 'active' : ''}`}
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            <Filter size={18} />
            Filters
            {filtersOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
        
        <div className="toolbar-right">
          <button className="toolbar-btn" onClick={fetchOrders} disabled={loading}>
            <RefreshCw size={18} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
          
          <button className="toolbar-btn export" onClick={exportToCSV}>
            <Download size={18} />
            Export CSV
          </button>

          <button className="toolbar-btn" onClick={openNewOrderModal} style={{ background: 'rgba(139,92,246,0.15)', borderColor: '#8b5cf6', color: '#c4b5fd' }}>
            <Plus size={18} />
            New Order
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {filtersOpen && (
        <div className="filters-panel">
          <div className="filter-group">
            <label>Status</label>
            <select 
              value={statusFilter} 
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="fulfilled">Fulfilled</option>
              <option value="shipped">Shipped</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Product Type</label>
            <select 
              value={productTypeFilter} 
              onChange={(e) => {
                setProductTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Types</option>
              <option value="original">Original</option>
              <option value="print">Print</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>Date From</label>
            <input 
              type="date" 
              value={dateRange.start}
              onChange={(e) => {
                setDateRange({ ...dateRange, start: e.target.value });
                setCurrentPage(1);
              }}
            />
          </div>
          
          <div className="filter-group">
            <label>Date To</label>
            <input 
              type="date" 
              value={dateRange.end}
              onChange={(e) => {
                setDateRange({ ...dateRange, end: e.target.value });
                setCurrentPage(1);
              }}
            />
          </div>
          
          <button 
            className="clear-filters"
            onClick={() => {
              setStatusFilter('all');
              setProductTypeFilter('all');
              setDateRange({ start: '', end: '' });
              setSearchQuery('');
              setCurrentPage(1);
            }}
          >
            Clear All
          </button>
        </div>
      )}

      {/* Orders Table */}
      <div className="orders-table-container">
        {loading ? (
          <div className="loading-state">
            <RefreshCw size={32} className="spinning" />
            <p>Loading orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="empty-state">
            <Package size={48} />
            <h3>No orders found</h3>
            <p>Try adjusting your filters or search query</p>
          </div>
        ) : (
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Product</th>
                <th>Type</th>
                <th>Customer</th>
                <th>Price</th>
                <th>Status</th>
                <th>Notifications</th>
                <th>Purchased</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {enrichedOrders.map((order) => (
                <tr key={order.id}>
                  <td className="order-num">
                    {order.order_num || <span className="no-value">-</span>}
                  </td>
                  <td className="product-name">
                    <div className="product-info with-thumb">
                      {order.artwork?.cover_image ? (
                        <img 
                          src={getThumbUrl(order.artwork.cover_image)} 
                          alt={order.product_name}
                          className="order-thumb"
                        />
                      ) : (
                        <div className="order-thumb-placeholder">
                          <Image size={20} />
                        </div>
                      )}
                      <span className="name">{order.product_name || 'Unknown'}</span>
                    </div>
                  </td>
                  <td>
                    <ProductTypeBadge type={order.product_type} />
                  </td>
                  <td className="customer">
                    <div className="customer-info">
                      <span className="name">{order.first_name} {order.last_name}</span>
                      <span className="email">{order.email}</span>
                    </div>
                  </td>
                  <td className="price">{formatCurrency(order.price)}</td>
                  <td>
                    <StatusBadge status={order.fulfillment_status} />
                  </td>
                  <td className="notifications">
                    <div className="notification-icons">
                      <span 
                        className={`notif-icon ${order.buyer_email_sent_at ? 'sent' : 'not-sent'}`}
                        title={order.buyer_email_sent_at ? `Buyer email sent ${formatDate(order.buyer_email_sent_at)}` : 'Buyer email not sent'}
                      >
                        <Mail size={14} />
                      </span>
                      <span 
                        className={`notif-icon ${order.buyer_push_sent_at ? 'sent' : 'not-sent'}`}
                        title={order.buyer_push_sent_at ? `Buyer push sent ${formatDate(order.buyer_push_sent_at)}` : 'Buyer push not sent'}
                      >
                        <Bell size={14} />
                      </span>
                      <span 
                        className={`notif-icon artist ${order.artist_email_sent_at ? 'sent' : 'not-sent'}`}
                        title={order.artist_email_sent_at ? `Artist email sent ${formatDate(order.artist_email_sent_at)}` : 'Artist email not sent'}
                      >
                        <Mail size={14} />
                      </span>
                    </div>
                  </td>
                  <td className="date">{formatDate(order.purchased_at)}</td>
                  <td className="actions">
                    <button 
                      className="action-btn view"
                      onClick={() => {
                        setSelectedOrder(order);
                        setDetailModalOpen(true);
                      }}
                      title="View Details"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="action-btn"
                      onClick={() => openEditOrderModal(order)}
                      title="Edit Order"
                      style={{ color: '#94a3b8' }}
                    >
                      <Edit size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(1)}
          >
            First
          </button>
          <button 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className="page-info">
            Page {currentPage} of {totalPages} ({totalCount} orders)
          </span>
          <button 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
          <button 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(totalPages)}
          >
            Last
          </button>
        </div>
      )}

      {/* Order Detail Modal */}
      {detailModalOpen && selectedOrder && (
        <div className="modal-overlay" onClick={() => setDetailModalOpen(false)}>
          <div className="order-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Order Details</h2>
              <button className="close-btn" onClick={() => setDetailModalOpen(false)}>×</button>
            </div>
            
            <div className="modal-content">
              {/* Order Info */}
              <div className="detail-section">
                <h3>Order Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Order Number</label>
                    <span>{selectedOrder.order_num || 'Not assigned'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status</label>
                    <StatusBadge status={selectedOrder.fulfillment_status} />
                  </div>
                  <div className="detail-item">
                    <label>Purchase Date</label>
                    <span>{formatDate(selectedOrder.purchased_at)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Stripe Payment ID</label>
                    <span className="stripe-id">
                      {selectedOrder.stripe_payment_id || 'N/A'}
                      {selectedOrder.stripe_payment_id && (
                        <a 
                          href={`https://dashboard.stripe.com/payments/${selectedOrder.stripe_payment_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="stripe-link"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Product Info */}
              <div className="detail-section">
                <h3>Product</h3>
                <div className="product-detail-with-image">
                  {selectedOrder.artwork?.cover_image && (
                    <img 
                      src={getThumbUrl(selectedOrder.artwork.cover_image)} 
                      alt={selectedOrder.product_name}
                      className="modal-artwork-thumb"
                    />
                  )}
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Product Name</label>
                      <span>{selectedOrder.product_name}</span>
                    </div>
                    <div className="detail-item">
                      <label>Type</label>
                      <ProductTypeBadge type={selectedOrder.product_type} />
                    </div>
                    <div className="detail-item">
                      <label>Price</label>
                      <span className="price-large">{formatCurrency(selectedOrder.price)}</span>
                    </div>
                    {selectedOrder.vc_id && (
                      <div className="detail-item">
                        <label>Artwork ID</label>
                        <span>
                          {selectedOrder.vc_id}
                          <button 
                            className="view-artwork-btn"
                            onClick={() => navigate(`/admin/artworks/${selectedOrder.vc_id}`)}
                          >
                            View Artwork
                          </button>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Customer (Buyer) Info */}
              <div className="detail-section">
                <h3>Buyer</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Name</label>
                    <span>{selectedOrder.first_name} {selectedOrder.last_name}</span>
                  </div>
                  <div className="detail-item">
                    <label>Email</label>
                    <span>
                      <a href={`mailto:${selectedOrder.email}`}>{selectedOrder.email}</a>
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Phone</label>
                    <span>
                      {selectedOrder.phone || selectedOrder.buyerInfo?.mobile_phone ? (
                        <a href={`tel:${selectedOrder.phone || selectedOrder.buyerInfo?.mobile_phone}`}>
                          {selectedOrder.phone || selectedOrder.buyerInfo?.mobile_phone}
                        </a>
                      ) : (
                        <span className="no-value">Not provided</span>
                      )}
                    </span>
                  </div>
                  {selectedOrder.buyerInfo && (
                    <div className="detail-item">
                      <label>VibrantCast User</label>
                      <span className="user-badge">✓ Registered</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Artist Info */}
              <div className="detail-section">
                <h3>Artist</h3>
                {selectedOrder.artistInfo ? (
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Legal Name</label>
                      <span>{selectedOrder.artistInfo.first_name} {selectedOrder.artistInfo.last_name}</span>
                    </div>
                    {selectedOrder.artistInfo.nickname && (
                      <div className="detail-item">
                        <label>Artist Name</label>
                        <span className="artist-nickname">{selectedOrder.artistInfo.nickname}</span>
                      </div>
                    )}
                    <div className="detail-item">
                      <label>Email</label>
                      <span>
                        <a href={`mailto:${selectedOrder.artistInfo.email}`}>{selectedOrder.artistInfo.email}</a>
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Phone</label>
                      <span>
                        {selectedOrder.artistInfo.mobile_phone ? (
                          <a href={`tel:${selectedOrder.artistInfo.mobile_phone}`}>{selectedOrder.artistInfo.mobile_phone}</a>
                        ) : (
                          <span className="no-value">Not provided</span>
                        )}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Status</label>
                      <span className="no-value">
                        {selectedOrder.artwork?.uuid 
                          ? `Artist UUID: ${selectedOrder.artwork.uuid} (not found in users)` 
                          : selectedOrder.vc_id 
                            ? 'No artist UUID on artwork' 
                            : 'No artwork linked to order'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Shipping Address */}
              {(selectedOrder.address_street || selectedOrder.address_city) && (
                <div className="detail-section">
                  <h3>Shipping Address</h3>
                  <div className="address-block">
                    <p>{selectedOrder.address_street}</p>
                    <p>
                      {selectedOrder.address_city}
                      {selectedOrder.address_state && `, ${selectedOrder.address_state}`}
                      {selectedOrder.address_zip && ` ${selectedOrder.address_zip}`}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Status Actions */}
              <div className="detail-section">
                <h3>Update Status</h3>
                <div className="status-actions">
                  {['pending', 'paid', 'fulfilled', 'shipped', 'cancelled'].map((status) => (
                    <button
                      key={status}
                      className={`status-action-btn ${selectedOrder.fulfillment_status === status ? 'active' : ''}`}
                      onClick={() => updateOrderStatus(selectedOrder.id, status)}
                      disabled={updating || selectedOrder.fulfillment_status === status}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notifications Section */}
              <div className="detail-section">
                <h3>Notifications</h3>
                <div className="notifications-status">
                  <div className="notif-row">
                    <div className="notif-item">
                      <Mail size={16} />
                      <span className="notif-label">Buyer Email:</span>
                      {selectedOrder.buyer_email_sent_at ? (
                        <span className="notif-sent">Sent {formatDate(selectedOrder.buyer_email_sent_at)}</span>
                      ) : (
                        <span className="notif-not-sent">Not sent</span>
                      )}
                    </div>
                    <div className="notif-item">
                      <Bell size={16} />
                      <span className="notif-label">Buyer Push:</span>
                      {selectedOrder.buyer_push_sent_at ? (
                        <span className="notif-sent">Sent {formatDate(selectedOrder.buyer_push_sent_at)}</span>
                      ) : (
                        <span className="notif-not-sent">Not sent</span>
                      )}
                    </div>
                  </div>
                  <div className="notif-row">
                    <div className="notif-item">
                      <Mail size={16} />
                      <span className="notif-label">Artist Email:</span>
                      {selectedOrder.artist_email_sent_at ? (
                        <span className="notif-sent">Sent {formatDate(selectedOrder.artist_email_sent_at)}</span>
                      ) : (
                        <span className="notif-not-sent">Not sent</span>
                      )}
                    </div>
                    <div className="notif-item">
                      <Bell size={16} />
                      <span className="notif-label">Artist Push:</span>
                      {selectedOrder.artist_push_sent_at ? (
                        <span className="notif-sent">Sent {formatDate(selectedOrder.artist_push_sent_at)}</span>
                      ) : (
                        <span className="notif-not-sent">Not sent</span>
                      )}
                    </div>
                  </div>
                  <button 
                    className="send-notifications-btn"
                    onClick={() => openConfirmationPreview(selectedOrder)}
                    disabled={sendingNotifications}
                  >
                    <Send size={16} />
                    {sendingNotifications ? 'Sending...' : 'Preview & Send Confirmation'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Confirmation Preview Modal */}
      {confirmationPreviewOpen && orderForConfirmation && (
        <PurchaseConfirmationPreview
          order={orderForConfirmation}
          artistInfo={orderForConfirmation.artistInfo}
          onClose={() => {
            setConfirmationPreviewOpen(false);
            setOrderForConfirmation(null);
          }}
          onSend={sendPurchaseConfirmation}
          sending={sendingNotifications}
        />
      )}

      {/* Create / Edit Order Modal */}
      {orderModalOpen && (
        <div className="modal-overlay" onClick={() => setOrderModalOpen(false)}>
          <div className="order-detail-modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingOrder ? 'Edit Order' : 'New Manual Order'}</h2>
              <button className="close-btn" onClick={() => setOrderModalOpen(false)}><X size={18} /></button>
            </div>
            <div className="modal-content" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Product */}
              <div className="detail-section">
                <h3>Product</h3>
                <div className="detail-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                    <label>Product Name *</label>
                    <input
                      type="text"
                      value={orderForm.product_name}
                      onChange={e => setOrderForm(f => ({ ...f, product_name: e.target.value }))}
                      placeholder="e.g. Raccoon sunset by krixy kraxi"
                      style={{ width: '100%', background: 'white', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px' }}
                    />
                  </div>
                  <div className="detail-item">
                    <label>Product Type *</label>
                    <select
                      value={orderForm.product_type}
                      onChange={e => setOrderForm(f => ({ ...f, product_type: e.target.value }))}
                      style={{ width: '100%', background: 'white', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px' }}
                    >
                      <option value="original">Original</option>
                      <option value="print">Print</option>
                      <option value="merch">Merch</option>
                    </select>
                  </div>
                  <div className="detail-item">
                    <label>Price *</label>
                    <input
                      type="number"
                      value={orderForm.price}
                      onChange={e => setOrderForm(f => ({ ...f, price: e.target.value }))}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      style={{ width: '100%', background: 'white', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px' }}
                    />
                  </div>
                  {orderForm.product_type === 'print' && (
                    <div className="detail-item">
                      <label>Print Size</label>
                      <input
                        type="text"
                        value={orderForm.print_size}
                        onChange={e => setOrderForm(f => ({ ...f, print_size: e.target.value }))}
                        placeholder="e.g. 8x10"
                        style={{ width: '100%', background: 'white', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px' }}
                      />
                    </div>
                  )}
                  {(orderForm.product_type === 'original' || orderForm.product_type === 'print') && (
                    <div className="detail-item">
                      <label>Artwork ID (vc_id)</label>
                      <input
                        type="number"
                        value={orderForm.vc_id}
                        onChange={e => setOrderForm(f => ({ ...f, vc_id: e.target.value }))}
                        placeholder="optional"
                        style={{ width: '100%', background: 'white', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px' }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Buyer */}
              <div className="detail-section">
                <h3>Buyer (optional)</h3>
                <div className="detail-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <div className="detail-item">
                    <label>First Name</label>
                    <input type="text" value={orderForm.first_name} onChange={e => setOrderForm(f => ({ ...f, first_name: e.target.value }))} style={{ width: '100%', background: 'white', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px' }} />
                  </div>
                  <div className="detail-item">
                    <label>Last Name</label>
                    <input type="text" value={orderForm.last_name} onChange={e => setOrderForm(f => ({ ...f, last_name: e.target.value }))} style={{ width: '100%', background: 'white', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px' }} />
                  </div>
                  <div className="detail-item">
                    <label>Email</label>
                    <input type="email" value={orderForm.email} onChange={e => setOrderForm(f => ({ ...f, email: e.target.value }))} style={{ width: '100%', background: 'white', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px' }} />
                  </div>
                  <div className="detail-item">
                    <label>Phone</label>
                    <input type="tel" value={orderForm.phone} onChange={e => setOrderForm(f => ({ ...f, phone: e.target.value }))} style={{ width: '100%', background: 'white', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px' }} />
                  </div>
                </div>
              </div>

              {/* Sale Details */}
              <div className="detail-section">
                <h3>Sale Details</h3>
                <div className="detail-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                  <div className="detail-item">
                    <label>Purchase Date/Time</label>
                    <input
                      type="datetime-local"
                      value={orderForm.purchased_at}
                      onChange={e => setOrderForm(f => ({ ...f, purchased_at: e.target.value }))}
                      style={{ width: '100%', background: 'white', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px' }}
                    />
                  </div>
                  <div className="detail-item">
                    <label>Fulfillment Status</label>
                    <select
                      value={orderForm.fulfillment_status}
                      onChange={e => setOrderForm(f => ({ ...f, fulfillment_status: e.target.value }))}
                      style={{ width: '100%', background: 'white', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px' }}
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="fulfilled">Fulfilled</option>
                      <option value="shipped">Shipped</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="detail-item">
                    <label>Sale Platform</label>
                    <input type="text" value={orderForm.sale_platform} onChange={e => setOrderForm(f => ({ ...f, sale_platform: e.target.value }))} style={{ width: '100%', background: 'white', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px' }} />
                  </div>
                  <div className="detail-item">
                    <label>Sale Location</label>
                    <input type="text" value={orderForm.sale_location} onChange={e => setOrderForm(f => ({ ...f, sale_location: e.target.value }))} style={{ width: '100%', background: 'white', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px' }} />
                  </div>
                  <div className="detail-item">
                    <label>Stripe Payment ID</label>
                    <input type="text" value={orderForm.stripe_payment_id} onChange={e => setOrderForm(f => ({ ...f, stripe_payment_id: e.target.value }))} placeholder="pi_..." style={{ width: '100%', background: 'white', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px' }} />
                  </div>
                  <div className="detail-item">
                    <label>Event ID</label>
                    <input type="number" value={orderForm.event_id} onChange={e => setOrderForm(f => ({ ...f, event_id: e.target.value }))} placeholder="e.g. 186" style={{ width: '100%', background: 'white', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px' }} />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="detail-section">
                <h3>Notes</h3>
                <textarea
                  value={orderForm.notes}
                  onChange={e => setOrderForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  placeholder="Internal notes..."
                  style={{ width: '100%', background: 'white', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px', resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
                <button
                  onClick={() => setOrderModalOpen(false)}
                  style={{ padding: '8px 18px', background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: 6, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={saveOrder}
                  disabled={savingOrder || !orderForm.product_name || !orderForm.price}
                  style={{ padding: '8px 18px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: (savingOrder || !orderForm.product_name || !orderForm.price) ? 0.5 : 1 }}
                >
                  {savingOrder ? 'Saving…' : editingOrder ? 'Save Changes' : 'Create Order'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersAdmin;
